import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The scroll-driven explanation of how AgenticScale works.
 *
 * Every panel is a screenshot of a real component from a running instance, in
 * the reader's own theme. The 3D is CSS transforms: the subjects are flat
 * rectangles, so a renderer would buy nothing and cost a library.
 *
 * Layout is two regions that cannot overlap by construction — copy beside the
 * stage on a wide screen, copy under it on a narrow one — because the previous
 * version placed panels as percentages of the whole frame and they ran across
 * the words on any display between a phone and a laptop.
 *
 * Panel sizes are in container-query units of the stage, so the same numbers
 * describe a 400px-tall band on a phone and an 800px one on a desktop without a
 * second set of values. Every panel also carries plain max-width/height limits,
 * which is what a browser without container units falls back to.
 */

interface Panel {
  src: string;
  alt: string;
  /** Centre of the panel, as a percentage of the stage — not of the page. */
  x: number;
  y: number;
  /** Height, in percent of the stage's height. */
  h: number;
  z: number;
  rotateY: number;
  rotateX?: number;
  at: number;
  /**
   * The frame's width ÷ height. The screenshot is drawn at the full width of
   * the frame, so this decides how much of a tall component is shown: at the
   * component's own aspect ratio all of it fits, and anything larger crops the
   * bottom. It must never be smaller than the screenshot's own ratio, or the
   * image would come up short and leave the frame part empty — which is what
   * the boundaries panel used to do.
   *
   * Every panel declares one. Sizing the frame from the image instead leaves
   * its width undefined at layout time, and the image ends up floating in a
   * box far larger than itself.
   */
  aspect: number;
  /**
   * Slides the image up inside the frame, in percent of the frame's width, to
   * show a band from the middle of a component rather than its top.
   *
   * Done with layout rather than a transform on purpose. `transform: scale()`
   * rasterises the image at its laid-out size and then stretches the result,
   * which is what made the panels look low-resolution, and it pushed the right
   * edge of the component outside the frame — which is why the outcome chips
   * were cut off.
   */
  shift?: number;
}

interface Beat {
  eyebrow: string;
  title: string;
  body: string;
  panels: Panel[];
}

const BEATS: Beat[] = [
  {
    eyebrow: 'The fleet',
    title: 'Every agent, and what it can reach',
    body: 'Each one has an owner, a purpose, a spending limit and a status. This is the dashboard, running with the sample fleet the download ships with.',
    panels: [
      { src: 'stats', alt: 'Total agents: 11 active — 9 protected, 1 monitoring, 1 quarantined', x: 36, y: 20, h: 21, z: -240, rotateY: -14, at: 0, aspect: 2.45 },
      { src: 'fleet', alt: 'Invoice & Payment Agent, owned by Finance & AP Team, status protected', x: 50, y: 50, h: 14, z: 0, rotateY: -12, at: 0.16, aspect: 4.40 },
      { src: 'profiles', alt: 'Active profiles, each with its status and spending limit', x: 68, y: 72, h: 26, z: -120, rotateY: -18, at: 0.4, aspect: 1.15 }
    ]
  },
  {
    eyebrow: 'Before you build',
    title: 'The boundaries, written down',
    body: 'What it may do, what it must never do, and the safeguards it has to run under. A profile is a file in your repository, reviewed like any other change.',
    panels: [
      { src: 'profile', alt: 'Profile editor for the Invoice & Payment Agent, with the control pack library', x: 42, y: 40, h: 50, z: -220, rotateY: -16, at: 0, aspect: 1.15 },
      { src: 'boundaries', alt: 'The allowed and blocked action lists, as edited in the profile', x: 58, y: 76, h: 12, z: 90, rotateY: -8, at: 0.3, aspect: 5.25 }
    ]
  },
  {
    eyebrow: 'At runtime',
    title: 'Allowed, held, or blocked',
    body: 'Every action is evaluated against the approved profile before it runs. The same input always reaches the same decision — no model sits in this path.',
    panels: [
      { src: 'decision-allow', alt: 'Decision: ALLOW — the action runs', x: 32, y: 20, h: 25, z: -180, rotateY: 10, at: 0, aspect: 1.55 },
      { src: 'decision-review', alt: 'Decision: REVIEW REQUIRED — the action waits for a person', x: 50, y: 50, h: 27, z: -20, rotateY: 1, at: 0.24, aspect: 1.55 },
      { src: 'decision-block', alt: 'Decision: BLOCK — the action never runs', x: 68, y: 74, h: 25, z: 120, rotateY: -12, at: 0.48, aspect: 1.55 }
    ]
  },
  {
    eyebrow: 'Not a verdict, a reason',
    title: 'And it tells you why',
    body: 'Which boundary, which safeguard, which framework control, and what happens next. A decision nobody can argue with is not much use to the person who has to answer for it.',
    panels: [
      { src: 'decision-block', alt: 'The reasons behind a block: the prohibited action, the guardrails matched, and what happens next', x: 50, y: 50, h: 62, z: 60, rotateY: -8, at: 0, aspect: 1.32, shift: -29 }
    ]
  },
  {
    eyebrow: 'Afterwards',
    title: 'Evidence you hold',
    body: 'Every decision recorded with a hash of exactly what was decided, in a SQLite file on your disk. Open it with any SQLite tool; there is no export to request.',
    panels: [
      { src: 'traces', alt: 'Execution traces: each agent action with its decision and payload hash', x: 50, y: 48, h: 48, z: 30, rotateY: -12, rotateX: 4, at: 0, aspect: 1.43 }
    ]
  },
  {
    eyebrow: 'Where it runs',
    title: 'All of it, on your machine',
    body: 'One archive. No account, no sign-in, no outbound connection — the test suite records every socket the hub opens and fails on any address that is not your own.',
    panels: []
  }
];

const SECTION_VH = 100 * (BEATS.length + 1);

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const smoothstep = (t: number) => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** The two screenshot sets differ only in the theme they were taken in. */
function mediaUrl(name: string, dark: boolean): string {
  return `/media/${dark ? 'dark' : 'light'}/${name}.webp`;
}

export const ScrollStory: React.FC = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  // Scroll position lives outside React. Rendering six beats and thirteen
  // transformed screenshots on every frame cannot hold 60fps: the work is
  // reconciliation and style recalculation, not painting. React lays the scene
  // out once; the frame loop below writes transform and opacity straight to the
  // elements, which the compositor can do without touching layout at all. The
  // only thing React still tracks is which beat is current, and that changes
  // six times over the whole section rather than sixty times a second.
  const [active, setActive] = useState(0);
  const [motion, setMotion] = useState<'pending' | 'scene' | 'static'>('pending');
  const [compact, setCompact] = useState(false);
  const [dark, setDark] = useState(false);

  // A pinned scene needs a band tall enough to hold a screenshot. A phone in
  // landscape gives it about 200px, where the panels cover the words no matter
  // how they are arranged — so that viewport gets the written version, which is
  // the same content laid out as cards.
  useEffect(() => {
    const decide = () => setMotion(
      prefersReducedMotion() || (window.innerHeight < 560 && window.innerWidth < 1024) ? 'static' : 'scene'
    );
    decide();
    window.addEventListener('resize', decide);
    return () => window.removeEventListener('resize', decide);
  }, []);

  // Wide enough to put the copy beside the stage rather than under it. The
  // threshold is the layout's, not a device's: an iPad in portrait and a small
  // laptop window are the same problem.
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const sync = () => setCompact(!query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  // The app writes the theme onto <html>, so the scene follows it rather than
  // sitting as a black band in the middle of a light page.
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setDark(root.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const readProgress = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return 0;
    const rect = section.getBoundingClientRect();
    const travel = rect.height - window.innerHeight;
    if (travel <= 0) return 0;
    return clamp01(-rect.top / travel);
  }, []);

  useEffect(() => {
    if (motion !== 'scene') return;
    const stage = stageRef.current;
    if (!stage) return;

    // Collected once. Each element carries what it needs in data attributes,
    // which React rewrites when the layout mode or theme changes; the loop
    // itself never queries the DOM or allocates.
    const layers = [...stage.querySelectorAll<HTMLElement>('[data-layer]')];
    const movers = [...stage.querySelectorAll<HTMLElement>('[data-mover]')].map((el) => ({
      el,
      kind: el.dataset.mover as 'panel' | 'window' | 'terminal',
      beat: Number(el.dataset.beat),
      at: Number(el.dataset.at ?? 0),
      z: Number(el.dataset.z ?? 0),
      turn: Number(el.dataset.turn ?? 0),
      tilt: Number(el.dataset.tilt ?? 0)
    }));

    const beatCount = BEATS.length;
    let frame = 0;
    let lastBeat = -1;
    // What the scene is showing, which chases where the scroll actually is.
    // A wheel notch moves the page in one jump of 50-120px, and a scene driven
    // straight off the scroll position jumps with it — the frame rate can be
    // perfect and the motion still looks stepped. Easing towards the target
    // instead turns each notch into a short glide. Snapping below a
    // sub-pixel delta keeps it from idling forever.
    let shown = -1;
    let running = false;

    const draw = () => {
      frame = 0;
      const target = readProgress();
      if (shown < 0) shown = target;
      const delta = target - shown;
      // Fast enough to stay with a flick, slow enough to smooth a notch.
      shown += Math.abs(delta) < 0.00025 ? delta : delta * 0.22;
      const settled = Math.abs(target - shown) < 0.00025;

      const progress = shown;
      const position = progress * beatCount;
      const current = Math.min(beatCount - 1, Math.floor(position + 0.0001));
      const local = clamp01(position - current);
      if (current !== lastBeat) { lastBeat = current; setActive(current); }

      for (let i = 0; i < layers.length; i += 1) {
        // A beat's distance runs 0 → -1 while it is on screen. It is fully
        // present from 0 to -0.8 and cross-dissolves with its neighbour over
        // the last fifth, eased at both ends. The hand-over used to take a
        // tenth of a beat and be linear, which is about 34px of scrolling —
        // enough for the change to read as a flash rather than a dissolve.
        // Longer than this and two full-size screenshots overlap for so long
        // that it looks like a muddle instead.
        const distance = i - Math.min(beatCount - 1, position);
        const presence = smoothstep(clamp01((distance + 1.0) / 0.2))
          * smoothstep(clamp01((0.2 - distance) / 0.2));
        layers[i].style.opacity = String(presence);
        // A layer nobody can see should not be composited either.
        layers[i].style.visibility = presence > 0.001 ? 'visible' : 'hidden';
      }

      for (const m of movers) {
        if (m.kind === 'panel') {
          // Continuous in the panel's own beat progress, so there is no step at
          // the boundary. The previous form asked whether this was the current
          // beat and, if so, offset the progress — which meant a panel with
          // `at: 0` evaluated to 0 one frame and 0.6 the next, snapping to
          // two-thirds opacity the instant its beat began. Starting a little
          // before the boundary lets the panel arrive during the dissolve
          // rather than into an empty stage.
          const own = position - m.beat;
          const arrival = smoothstep(clamp01((own + 0.28 - m.at) / 0.4));
          const settle = 1 - arrival;
          // A panel used to arrive in the first third of its beat and then sit
          // perfectly still for the rest of it, so two thirds of every scroll
          // moved nothing on screen and the section read as a stack of slides
          // rather than a scene. Each panel now also drifts slowly through its
          // whole beat, by an amount that depends on how far away it is, so the
          // deck keeps opening as you scroll and near panels pass far ones.
          const through = clamp01(position - m.beat) - 0.5;
          const depth = 1 + m.z / 420;
          m.el.style.opacity = String(arrival);
          m.el.style.transform =
            `translate3d(calc(-50% + ${(settle * 5 - through * 1.6 * depth).toFixed(2)}%), calc(-50% + ${(settle * 7 - through * 4.5 * depth).toFixed(2)}%), ${(m.z - settle * 220 + through * 70 * depth).toFixed(1)}px)`
            + ` rotateY(${(m.turn - settle * 8 + through * 2.2).toFixed(2)}deg)`
            + ` rotateX(${(m.tilt + settle * 3 - through * 1.1).toFixed(2)}deg)`;
        } else {
          // Their own beat's progress, never the current beat's. Reading
          // `local` meant these two were fully arrived 40% of the way through
          // beat *five* — invisible only because their layer was still faded
          // out. As the layer crossfaded in they appeared already formed, and
          // then `local` reset at the boundary and snapped them back to the
          // start to animate a second time. That is the pop.
          const own = position - m.beat;
          // The same pre-roll the panels get. Without it the previous beat had
          // faded out before these had begun, and the hand-over passed through
          // a moment with nothing on the stage at all. The size of it is set by
          // that overlap: the outgoing beat drops out of sight about 93% of the
          // way through itself, so the incoming one has to be visible before
          // then.
          const settle = m.kind === 'window'
            ? 1 - smoothstep(clamp01((own + 0.28) / 0.4))
            : 1 - smoothstep(clamp01((own + 0.28 - 0.3) / 0.4));
          const through = clamp01(own) - 0.5;
          m.el.style.opacity = String(1 - settle);
          m.el.style.transform = m.kind === 'window'
            ? `translate3d(calc(-50% + ${(settle * 5 - through * 1.2).toFixed(2)}%), calc(-50% + ${(-through * 3).toFixed(2)}%), ${(40 - settle * 200 + through * 60).toFixed(1)}px)`
              + ` rotateY(${(m.turn - settle * 7 + through * 2).toFixed(2)}deg)`
            : `translate3d(-50%, calc(-50% + ${(settle * 12 - through * 5).toFixed(2)}%), ${(150 + through * 40).toFixed(1)}px)`
              + ` rotateY(${(m.turn + through * 1.6).toFixed(2)}deg)`;
        }
      }

      // Keep going while the scene is still catching up; stop once it has, so
      // an idle page is not running a frame loop.
      if (settled) { running = false; return; }
      frame = requestAnimationFrame(draw);
    };

    // One loop at a time: the scroll listener starts it, and it runs itself
    // until the scene has caught up.
    const kick = () => { if (!running) { running = true; frame = requestAnimationFrame(draw); } };
    const onResize = () => { shown = -1; kick(); };
    running = true;
    draw();
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', kick);
      window.removeEventListener('resize', onResize);
    };
  }, [motion, compact, dark, readProgress]);

  if (motion === 'static') {
    return (
      <section className="space-y-4" aria-label="How AgenticScale works">
        {BEATS.map((beat, index) => (
          <article key={beat.title} className="glass-panel overflow-hidden rounded-3xl p-6 sm:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 dark:text-brand-300">
              {String(index + 1).padStart(2, '0')} · {beat.eyebrow}
            </p>
            <h3 className="mt-2 text-xl font-black tracking-tight text-slate-900 dark:text-white">{beat.title}</h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-slate-300">{beat.body}</p>
            <img
              src={mediaUrl(beat.panels[0]?.src ?? 'window', dark)}
              alt={beat.panels[0]?.alt ?? 'The AgenticScale dashboard running on a local machine'}
              loading="lazy"
              decoding="async"
              className="mt-5 w-full rounded-xl border border-slate-900/10 dark:border-white/10"
            />
          </article>
        ))}
      </section>
    );
  }

  const stageSurface = dark
    ? 'bg-[#05070f]'
    : 'bg-[linear-gradient(180deg,#f1f5fb_0%,#e6edf8_100%)]';
  const stageGlow = dark
    ? 'bg-[radial-gradient(90%_70%_at_70%_36%,rgba(37,99,235,0.20),transparent_62%),radial-gradient(70%_60%_at_18%_78%,rgba(129,140,248,0.12),transparent_70%)]'
    : 'bg-[radial-gradient(90%_70%_at_70%_36%,rgba(37,99,235,0.10),transparent_60%),radial-gradient(70%_60%_at_18%_78%,rgba(99,102,241,0.07),transparent_70%)]';
  const panelFrame = dark
    ? 'border-white/15 shadow-[0_50px_120px_-25px_rgba(0,0,0,0.95)]'
    : 'border-slate-900/10 shadow-[0_40px_90px_-30px_rgba(15,23,42,0.45)]';

  return (
    <section
      ref={sectionRef}
      style={{ height: `${SECTION_VH}vh` }}
      className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-[100vw] max-w-[100vw] overflow-x-clip"
      aria-label="How AgenticScale works"
    >
      {/* Starts below the navigation, not underneath it. The header is sticky at
          top-0 with z-50, so a stage that also pins to top-0 spends its first
          64px hidden behind it — which cut the top off every panel and left the
          stacked layout with nowhere to put them. */}
      <div className={`sticky top-16 h-[calc(100svh-4rem)] overflow-hidden ${stageSurface}`}>
        <div className={`pointer-events-none absolute inset-0 ${stageGlow}`} />

        {/* The stage: the right half on a wide screen, the top half otherwise.
            Panels are positioned inside it, so they cannot reach the copy. */}
        <div
          ref={stageRef}
          className="absolute inset-x-0 top-0 bottom-60 sm:bottom-56 lg:inset-y-0 lg:bottom-0 lg:left-[42%] lg:right-0"
          style={{ perspective: '1500px', perspectiveOrigin: '50% 45%', containerType: 'size' }}
          aria-hidden="true"
        >
          {BEATS.map((beat, index) => (
            <div key={beat.title} data-layer={index} className="absolute inset-0" style={{ transformStyle: 'preserve-3d', opacity: 0 }}>
              {beat.panels.map((panel) => {
                const turn = compact ? panel.rotateY * 0.5 : panel.rotateY;
                // Stacked, the stage is one column rather than a wide field, so
                // the arrangement pulls into a tighter deck and each panel is
                // larger — the same fan spread across a phone reads as clutter.
                const x = compact ? 50 + (panel.x - 50) * 0.3 : panel.x;
                const y = compact ? 50 + (panel.y - 50) * 0.26 : panel.y;
                const h = compact ? panel.h * 1.45 : panel.h;
                // The frame's size is stated outright — height from the beat,
                // width from the panel's own ratio — so it never has to be
                // inferred from the image it holds.
                // Width first, then the height follows from the stated ratio.
                // Setting both independently meant that whenever the 86cqw clamp
                // won — which is most of the time on a narrow screen — the frame
                // quietly took a different shape from the one declared, and the
                // crop, measured in percent of that width, slid with it.
                const size = {
                  // The last term caps the height the ratio derives, in the
                  // stage's own units — `94%` there would cap the width instead.
                  width: `min(86cqw, ${(h * panel.aspect).toFixed(1)}cqh, ${(94 * panel.aspect).toFixed(1)}cqh)`,
                  aspectRatio: String(panel.aspect)
                };
                return (
                  <figure
                    key={panel.src + panel.x}
                    data-mover="panel"
                    data-beat={index}
                    data-at={panel.at}
                    data-z={panel.z}
                    data-turn={turn}
                    data-tilt={panel.rotateX ?? 0}
                    className="absolute m-0"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      opacity: 0,
                      transformStyle: 'preserve-3d',
                      // Its own compositor layer, so a frame is a composite
                      // rather than a repaint of a large image and the long
                      // soft shadow under it.
                      willChange: 'transform, opacity'
                    }}
                  >
                    <div className={`relative overflow-hidden rounded-2xl border ${panelFrame}`} style={size}>
                      <img
                        src={mediaUrl(panel.src, dark)}
                        alt={panel.alt}
                        loading="lazy"
                        decoding="async"
                        className="block"
                        // Full width, always: the component is never cut off
                        // sideways, and the browser rasterises it at the size
                        // it is drawn rather than scaling a smaller bitmap.
                        style={{ width: '100%', marginTop: panel.shift ? `${panel.shift}%` : undefined }}
                      />
                      {/* A panel shows a band of a component taller than its
                          frame, so the cut lands wherever it lands — usually
                          through the middle of a line of text, or across the
                          edge of a coloured banner, both of which read as a
                          rendering fault rather than as a crop. Softening the
                          edge makes it say "there is more of this" instead. The
                          top only needs it when the band starts mid-component. */}
                      {panel.shift ? (
                        <div
                          className="pointer-events-none absolute inset-x-0 top-0 h-[10%]"
                          style={{
                            background: dark
                              ? 'linear-gradient(to bottom, rgba(5,7,15,0.92), rgba(5,7,15,0))'
                              : 'linear-gradient(to bottom, rgba(255,255,255,0.92), rgba(255,255,255,0))'
                          }}
                        />
                      ) : null}
                      <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-[14%]"
                        style={{
                          background: dark
                            ? 'linear-gradient(to top, rgba(5,7,15,0.92), rgba(5,7,15,0))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.92), rgba(255,255,255,0))'
                        }}
                      />
                    </div>
                  </figure>
                );
              })}

              {index === BEATS.length - 1 && (
                <>
                  <figure
                    data-mover="window"
                    data-beat={index}
                    data-turn={compact ? -5 : -11}
                    className="absolute m-0"
                    style={{
                      left: '48%',
                      top: '42%',
                      width: 'min(80cqw, 128cqh)',
                      opacity: 0,
                      transformStyle: 'preserve-3d',
                      willChange: 'transform, opacity'
                    }}
                  >
                    <div className={`overflow-hidden rounded-xl border ${panelFrame} ${dark ? 'bg-slate-950' : 'bg-white'}`}>
                      <div className={`flex items-center gap-2 border-b px-3 py-2 ${dark ? 'border-white/10 bg-slate-900/80' : 'border-slate-900/10 bg-slate-100'}`}>
                        <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                        <span className={`ml-3 flex-1 truncate rounded-md px-3 py-1 text-left font-mono text-[0.65rem] ${dark ? 'bg-slate-950/80 text-slate-400' : 'bg-white text-slate-500'}`}>
                          127.0.0.1:7777/dashboard
                        </span>
                      </div>
                      <img src={mediaUrl('window', dark)} alt="The AgenticScale dashboard running on a local machine" className="block w-full" loading="lazy" decoding="async" />
                    </div>
                  </figure>

                  <figure
                    data-mover="terminal"
                    data-beat={index}
                    data-turn={compact ? -4 : -9}
                    className="absolute m-0"
                    style={{
                      left: compact ? '50%' : '70%',
                      // Stacked, the stage is a band rather than a column, and
                      // the terminal is a block of text whose height barely
                      // changes with width — so at 82% its resting position ran
                      // past the bottom of the stage and into the words. It only
                      // ever looked fine because the old timing never let it
                      // finish arriving.
                      top: compact ? '70%' : '80%',
                      width: 'min(52cqw, 62cqh)',
                      opacity: 0,
                      transformStyle: 'preserve-3d',
                      willChange: 'transform, opacity'
                    }}
                  >
                    <div className={`overflow-hidden rounded-xl border ${panelFrame} ${dark ? 'bg-[#05070f]' : 'bg-slate-900'}`}>
                      <div className="border-b border-white/10 px-3 py-1.5 font-mono text-[0.6rem] uppercase tracking-widest text-slate-500">terminal</div>
                      <pre className="px-4 py-3 text-left font-mono text-[0.68rem] leading-relaxed text-slate-300">
<span className="text-emerald-400">$</span> ./agenticscale{'\n\n'}
<span className="text-slate-400">  AgenticScale hub</span>{'\n'}
<span className="text-sky-300">  http://127.0.0.1:7777</span>{'\n\n'}
<span className="text-slate-500">  data      ~/.agenticscale/hub.db</span>{'\n'}
<span className="text-slate-500">  network   nothing leaves this machine</span>
                      </pre>
                    </div>
                  </figure>
                </>
              )}
            </div>
          ))}
        </div>

        {/* The copy: under the stage on a narrow screen, beside it on a wide
            one. Never over it. */}
        <div className="absolute inset-x-0 bottom-0 h-60 flex items-center px-6 pb-8 sm:h-56 sm:px-10 sm:pb-6 lg:inset-y-0 lg:right-auto lg:h-auto lg:w-[42%] lg:px-10 lg:pb-0 xl:px-16">
          <div className="relative h-[13rem] w-full max-w-xl sm:h-[12rem] lg:h-[19rem] lg:max-w-none">
            {BEATS.map((beat, index) => (
              <div
                key={beat.title}
                className={`absolute inset-x-0 top-0 transition-all duration-500 ease-out ${
                  index === active ? 'translate-y-0 opacity-100' : index < active ? '-translate-y-4 opacity-0' : 'translate-y-4 opacity-0'
                }`}
                aria-hidden={index !== active}
              >
                <p className="text-[0.7rem] font-bold uppercase tracking-[0.25em] text-brand-600 dark:text-brand-300">
                  {String(index + 1).padStart(2, '0')} · {beat.eyebrow}
                </p>
                <h3 className="mt-3 text-2xl font-black leading-tight tracking-tight text-slate-900 dark:text-white sm:text-3xl lg:text-[2.6rem]">
                  {beat.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300 lg:mt-4 lg:text-base">
                  {beat.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="pointer-events-none absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-1.5">
          {BEATS.map((beat, index) => (
            <span
              key={beat.title}
              className={`h-1 rounded-full transition-all duration-500 ${
                index === active
                  ? 'w-8 bg-slate-900/70 dark:bg-white/80'
                  : 'w-2 bg-slate-900/20 dark:bg-white/25'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
